import { Observable, ObservedValueOf, OperatorFunction, catchError, of, throwError } from 'rxjs';

import { HisBackendRequestOptions, HisBackendService } from './his-backend.service';
import { HisNavigationService } from '../base/his-navigation.service';
import { inject } from '@angular/core';

/**
 * a generic service to load, save and delete an entity.
 */
export abstract class HisGenericEntityService<T> {
    private hisBackendService = inject(HisBackendService);
    private hisNavigationService = inject(HisNavigationService);
    private apiPrefix: string;

    constructor(apiPrefix: string);
    /** @deprecated */
    constructor(
        backendService: HisBackendService,
        apiPrefix: string,
        navigationService?: HisNavigationService
    );
    // This is the implementation which cannot be invoked directly.
    // The above declarations ensure that this constructor is either called with only one argument of type string
    // or at least two arguments.
    constructor(
        first?: HisBackendService | string,
        second?: string,
        _navigationService?: HisNavigationService
    ) {
        if (second) {
            this.apiPrefix = second;
        } else {
            this.apiPrefix = first as string;
        }
    }

    /**
     * 
     * @param navigationService 
     * @returns an operator function to be used in Observable.pipe() to redirect to the access denied page if the user has no permission to view the entity.
     */
    private redirectToAccessDenied<X, O>( navigationService: HisNavigationService ): OperatorFunction<X, X | ObservedValueOf<O>> {
        return catchError( error => {
            // navigate to access denied page if the user has no permission to view the entity
            if ( error.status === 401 || error.status === 403 ) {
                navigationService.navigateToAccessDenied();
                return of();
            }

            //propagate the error if it is not a permission error
            return throwError( () => error );
        } );
    }

    /**
     * creates an empty entity object
     */
    createEmptyEntity(): T {
        return {} as T;
    }

    /**
     * loads the entity from the backend. May display errors to users.
     */
    load( id: number, redirectAccessDenied = true ): Observable<T> {
        return this.getData( id, redirectAccessDenied );
    }


    /**
     * loads the entity from the backend with all details. May display errors to users.
     */
    loadWithDetails( id: number, redirectAccessDenied = true ): Observable<T> {
        return this.getData( id, redirectAccessDenied, '/details' );
    }

    private getData( id: number, redirectAccessDenied: boolean, apiSuffix?: string ): Observable<T> {
        const requestOptions: HisBackendRequestOptions = { showPermissionError: !redirectAccessDenied };
        const url = apiSuffix ? `${this.apiPrefix}/${id}${apiSuffix}` : `${this.apiPrefix}/${id}`;
        const apiCall = this.hisBackendService.getData( url, requestOptions );
        if ( redirectAccessDenied ) {
            return apiCall.pipe( this.redirectToAccessDenied( this.hisNavigationService ) );
        }

        return apiCall;
    }

    /**
     * saves the entity to the backend. May display errors to users.
     */
    save( t: T ): Observable<T> {
        return this.hisBackendService.saveData( this.apiPrefix, t );
    }

    /**
     * verifies that the entity may be deleted (e. g. not referenced by other objects).
     * May display errors to users.
     */
    ensureDeletable( id: number ): Observable<void> {
        return this.hisBackendService.getData( `${this.apiPrefix}/${id}/ensureDeletable` );
    }

    /**
     * does the current user have permission to edit this entity?
     */
    mayEdit( id: number ): Observable<boolean> {
        return this.hisBackendService.getData( `${this.apiPrefix}/${id}/mayEdit` );
    }

    /**
     * deletes the entity on the backend. May display errors to users.
     */
    delete( id: number ): Observable<void> {
        return this.hisBackendService.delete( `${this.apiPrefix}/${id}` );
    }

    /**
     * list entities matching the searchText. May display errors to users.
     */
    list( searchText: string ): Observable<T[]> {
        return this.hisBackendService.getData( `${this.apiPrefix}?q=${encodeURI( searchText )}` );
    }

}
